import styled, { keyframes } from "styled-components";

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const checkAnimation = keyframes`
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
`;

const bounceIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.5);
  }
  60% {
    opacity: 1;
    transform: scale(1.15);
  }
  100% {
    transform: scale(1);
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(40, 167, 69, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0);
  }
`;

const pulseRed = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(220, 53, 69, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
  }
`;

export const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: 100vh;
  background: var(--background);

  @media (max-width: 768px) {
    padding: 20px 15px;
  }
`;

export const SuccessHeader = styled.div`
  text-align: center;
  padding: 60px 0;
  animation: ${fadeInUp} 0.8s ease-out;

  @media (max-width: 768px) {
    padding: 40px 0;
  }
`;

export const SuccessIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
  background: var(--success);
  border-radius: 50%;
  margin-bottom: 30px;
  animation: ${checkAnimation} 0.6s ease-out 0.3s both;
  box-shadow: 0 10px 30px rgba(40, 167, 69, 0.3);

  svg {
    font-size: 60px;
    color: var(--surface);
  }

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;

    svg {
      font-size: 40px;
    }
  }
`;

export const SuccessTitle = styled.h1`
  font-size: 48px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 20px;
  text-transform: uppercase;
  letter-spacing: 2px;

  @media (max-width: 768px) {
    font-size: 32px;
    letter-spacing: 1px;
  }
`;

export const SuccessMessage = styled.p`
  font-size: 18px;
  color: var(--text-secondary);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

export const OrderDetails = styled.div`
  display: grid;
  gap: 30px;
  margin-bottom: 50px;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;

  @media (max-width: 768px) {
    gap: 25px;
    margin-bottom: 40px;
  }
`;

export const OrderSection = styled.div`
  background: var(--surface);
  border-radius: 20px;
  padding: 30px;
  border: 2px solid var(--accent-light);
  box-shadow: var(--shadow-soft);
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 15px;
  }
`;

export const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 25px;
  text-transform: uppercase;
  letter-spacing: 1px;

  svg {
    color: var(--primary);
    font-size: 24px;
  }

  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 20px;
  }
`;

export const OrderInfo = styled.div`
  display: grid;
  gap: 15px;
`;

export const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid var(--accent-light);

  &:last-child {
    border-bottom: none;
  }
`;

export const InfoLabel = styled.span`
  font-weight: 500;
  color: var(--text-secondary);
  font-size: 16px;
`;

export const InfoValue = styled.span`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 16px;

  &.status-completed {
    color: var(--success);
    background: rgba(40, 167, 69, 0.1);
    padding: 4px 12px;
    border-radius: 15px;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  &.total {
    color: var(--primary);
    font-size: 20px;
    font-weight: 700;
  }
`;

export const OrderItems = styled.div`
  display: grid;
  gap: 15px;
`;

export const OrderItem = styled.div`
  display: flex;
  gap: 15px;
  padding: 20px;
  background: var(--background);
  border-radius: 15px;
  border: 1px solid var(--accent-light);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    padding: 15px;
  }
`;

export const ItemImage = styled.div`
  width: 80px;
  height: 60px;
  border-radius: 10px;
  background-size: cover;
  background-position: center;
  border: 2px solid var(--accent-light);
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    height: 120px;
  }
`;

export const ItemDetails = styled.div`
  flex: 1;

  p {
    color: var(--text-secondary);
    font-size: 14px;
    line-height: 1.4;
    margin: 8px 0 0 0;
  }
`;

export const ItemName = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 5px 0;
  line-height: 1.3;
`;

export const ItemPrice = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: var(--primary);
  align-self: flex-start;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

export const NextSteps = styled.div`
  margin-bottom: 50px;
  animation: ${fadeInUp} 0.8s ease-out 0.4s both;

  .steps-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 25px;
    margin-top: 30px;
  }

  @media (max-width: 768px) {
    margin-bottom: 40px;

    .steps-grid {
      grid-template-columns: 1fr;
      gap: 20px;
    }
  }
`;

export const StepCard = styled.div`
  background: var(--surface);
  border-radius: 20px;
  padding: 25px;
  border: 2px solid var(--accent-light);
  box-shadow: var(--shadow-soft);
  transition: all 0.3s ease;
  animation: ${bounceIn} 0.6s ease-out;

  &:hover {
    transform: translateY(-5px);
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

export const StepIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: var(--gradient-primary);
  border-radius: 50%;
  margin-bottom: 15px;

  svg {
    font-size: 24px;
    color: var(--surface);
  }
`;

export const StepContent = styled.div`
  h4 {
    font-size: 18px;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 10px;
  }

  p {
    font-size: 14px;
    color: var(--text-secondary);
    line-height: 1.5;
    margin: 0;
  }
`;

export const ActionButtons = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-bottom: 50px;
  animation: ${fadeInUp} 0.8s ease-out 0.6s both;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    margin-bottom: 40px;
  }
`;

export const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 15px 30px;
  background: var(--gradient-primary);
  color: var(--surface);
  border: none;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: var(--shadow-soft);

  &:hover {
    transform: translateY(-2px);
  }

  svg {
    font-size: 18px;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 18px 20px;
  }
`;

export const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 15px 30px;
  background: transparent;
  color: var(--accent);
  border: 2px solid var(--accent);
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--accent);
    color: var(--surface);
    transform: translateY(-2px);
  }

  svg {
    font-size: 18px;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 18px 20px;
  }
`;

export const ContactInfo = styled.div`
  background: var(--surface);
  border-radius: 20px;
  padding: 30px;
  border: 2px solid var(--accent-light);
  box-shadow: var(--shadow-soft);
  text-align: center;
  animation: ${fadeInUp} 0.8s ease-out 0.8s both;

  h3 {
    font-size: 24px;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 15px;
    text-transform: uppercase;
  }

  .contact-subtitle {
    font-size: 16px;
    color: var(--text-secondary);
    margin-bottom: 30px;
    line-height: 1.5;
  }

  .contact-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 25px;
    margin-bottom: 25px;
  }

  .contact-item {
    display: flex;
    align-items: flex-start;
    gap: 15px;
    padding: 20px;
    background: var(--background);
    border-radius: 15px;
    border: 1px solid var(--accent-light);
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
    }

    svg {
      font-size: 24px;
      color: var(--primary);
      flex-shrink: 0;
      margin-top: 2px;
    }

    div {
      text-align: left;
      flex: 1;

      strong {
        display: block;
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: 8px;
        font-size: 16px;
      }

      p {
        margin: 0 0 5px 0;
        color: var(--text-primary);
        font-size: 15px;
        font-weight: 500;
      }

      .contact-note {
        font-size: 13px;
        color: var(--text-muted);
        font-style: italic;
      }

      .business-hours {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 5px;
      }

      .status-indicator {
        min-width: 8px;
        max-width: 8px;
        min-height: 8px;
        max-height: 8px;
        border-radius: 50%;
        flex-shrink: 0;

        &.open {
          background-color: var(--success);
          animation: ${pulse} 2s infinite;
        }

        &.closed {
          background-color: var(--error);
          animation: ${pulseRed} 2s infinite;
        }
      }
    }
  }

  .additional-info {
    background: var(--background);
    border-radius: 15px;
    padding: 20px;
    border: 1px solid var(--accent-light);
    text-align: left;

    p {
      margin: 0;
      color: var(--text-secondary);
      font-size: 14px;
      line-height: 1.5;

      strong {
        color: var(--text-primary);
        font-weight: 600;
      }
    }
  }

  @media (max-width: 768px) {
    padding: 25px 20px;

    .contact-grid {
      grid-template-columns: 1fr;
      gap: 20px;
    }

    .contact-item {
      text-align: center;
      flex-direction: column;
      gap: 12px;

      div {
        text-align: center;

        .business-hours {
          justify-content: center;
        }
      }
    }

    .additional-info {
      text-align: center;
    }
  }
`;
